import { isPlatformBrowser } from '@angular/common';
import type {
  AfterContentInit,
  AfterViewChecked,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import {
  ProgressBarComponent,
  ProgressOrientation,
  ProgressSize,
} from '@freelancer/ui/progress-bar';
import { fromEvent, Subscription } from 'rxjs';
import { ProgressStepItemComponent } from './progress-step-item.component';
import {
  ProgressStepsBackgroundColor,
  ProgressStepsType,
} from './progress-steps.types';

@Component({
  standalone: true,
  imports: [ProgressBarComponent],
  selector: 'fl-progress-steps',
  template: `
    <div
      #progressStepBar
      class="ProgressStepBar"
      [attr.data-orientation]="orientation"
      [attr.data-mobile-large-indicator]="mobileLargeIndicator"
    >
      <fl-progress-bar
        [label]="label"
        [progressPercentage]="currentStepPercentage"
        [orientation]="orientation"
        [size]="ProgressSize.XSMALL"
      ></fl-progress-bar>
    </div>
    <div
      class="ProgressStepRow"
      [attr.data-orientation]="orientation"
    >
      <ng-content select="fl-progress-step-item"></ng-content>
    </div>
  `,
  styleUrls: ['./progress-steps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressStepsComponent
  implements OnChanges, AfterContentInit, OnDestroy, OnInit, AfterViewChecked
{
  private platformId = inject(PLATFORM_ID);
  private changeDetector = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private timeUtils = inject(TimeUtils);

  ProgressSize = ProgressSize;

  currentStepPercentage = 0;
  height: number;

  @Input() currentStep = 1;
  @Input() startStep = 1;
  @Input() alwaysShowItemsContent = false;
  @Input() backgroundColor = ProgressStepsBackgroundColor.DEFAULT;

  @Input() orientation: ProgressOrientation = ProgressOrientation.HORIZONTAL;

  @Input() label: string;

  /** List of steps that are skipped */
  @Input() skippedSteps?: readonly number[];

  @ContentChildren(ProgressStepItemComponent)
  progressStepItem: QueryList<ProgressStepItemComponent>;

  @ViewChild('progressStepBar', { read: ElementRef })
  progressStepBar: ElementRef<HTMLDivElement>;

  @Input()
  mobileLargeIndicator = false;

  @Input() stepItemType = ProgressStepsType.NUMBER;
  @Input() smallDot = false;

  private previousOffsetTop: number;
  private subscriptions = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    if ('currentStep' in changes && this.progressStepItem) {
      this.setChildrenProperties();
    }

    if ('orientation' in changes && this.progressStepItem) {
      this.setChildrenOrientation();
      this.setChildrenProperties();
    }

    if ('skippedSteps' in changes && this.progressStepItem) {
      this.setChildrenProperties();
    }

    if ('startStep' in changes && this.progressStepItem) {
      this.setChildrenProperties();
    }

    if ('mobileLargeIndicator' in changes && this.progressStepItem) {
      this.setChildrenProperties();
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Adjust progress bar when window is resized
    this.subscriptions.add(
      fromEvent(window, 'resize')
        .pipe(this.timeUtils.rxDebounceTime(150))
        .subscribe(() => {
          this.recalculateProgressBar();
        }),
    );
  }

  ngAfterContentInit(): void {
    this.setChildrenOrientation();
    this.setChildrenProperties();
    this.setSubstepSubscription();
  }

  /**
   * Adjust progress bar whenever the view changes
   */
  ngAfterViewChecked(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.recalculateProgressBar();
  }

  setProgressPercentage(): void {
    if (this.orientation === ProgressOrientation.HORIZONTAL) {
      this.setHorizontalPercentage();
    } else {
      this.setVerticalPercentage();
    }
  }

  setHorizontalPercentage(): void {
    const stepPercentage =
      (this.currentStep - this.startStep) / (this.progressStepItem.length - 1);
    this.currentStepPercentage = Math.min(Math.max(stepPercentage, 0), 1) * 100;
  }

  setVerticalPercentage(): void {
    // FIXME: T267853 - We can't calculate the height of progress on the server.
    if (
      isPlatformBrowser(this.platformId) &&
      this.currentStep - this.startStep + 1 > 0
    ) {
      const progressStepItems = this.progressStepItem.toArray();

      // Current step should not exceed number of steps
      const step = Math.min(this.currentStep, progressStepItems.length);

      const stepItem = progressStepItems[step - 1];
      const subStepPosition = this.getSubstepPosition(stepItem);

      const progressHeight =
        parseInt(stepItem.elementRef.nativeElement.offsetTop, 10) +
        subStepPosition;

      this.currentStepPercentage = (progressHeight / this.height) * 100;
    } else {
      this.currentStepPercentage = 0;
    }
  }

  setProgressBarHeight(): void {
    const { nativeElement } = this.progressStepBar;

    if (this.orientation === ProgressOrientation.VERTICAL) {
      this.height =
        this.progressStepItem.last.elementRef.nativeElement.offsetTop;

      this.renderer.setStyle(nativeElement, 'height', `${this.height}px`);
    } else {
      this.renderer.removeStyle(nativeElement, 'height');
    }
  }

  setChildrenProperties(): void {
    this.progressStepItem.forEach((component, index) => {
      this.setChildrenActiveState(component, index);
      this.toggleChildrenDescription(component, index);

      component.isLastStep = index === this.progressStepItem.length - 1;
      component.stepNumber = index + this.startStep;
      component.backgroundColor = this.backgroundColor;
      component.stepItemType = this.stepItemType;
      component.smallDot = this.smallDot;
      component.mobileLargeIndicator = this.mobileLargeIndicator;

      component.changeDetectorRef.markForCheck();
    });

    setTimeout(() => {
      this.setProgressBarHeight();
      this.setProgressPercentage();
      this.changeDetector.markForCheck();
    });
  }

  setSubstepSubscription(): void {
    this.progressStepItem.forEach((component, index) => {
      this.subscriptions.add(
        component.substepChanged.subscribe(() => {
          if (index === this.currentStep - this.startStep) {
            this.handleSubstepChanges();
          }
        }),
      );
    });
  }

  setChildrenOrientation(): void {
    this.progressStepItem.forEach(component => {
      component.orientation = this.orientation;
    });
  }

  setChildrenActiveState(
    component: ProgressStepItemComponent,
    index: number,
  ): void {
    component.done = index < this.currentStep - this.startStep;
    component.currentStep = index === this.currentStep - this.startStep;
    component.skipped =
      !!this.skippedSteps &&
      this.skippedSteps.length > 0 &&
      this.skippedSteps.map(step => step - this.startStep).includes(index) &&
      !component.currentStep;
  }

  toggleChildrenDescription(
    component: ProgressStepItemComponent,
    index: number,
  ): void {
    if (
      (this.alwaysShowItemsContent ||
        index === this.currentStep - this.startStep) &&
      this.orientation === ProgressOrientation.VERTICAL
    ) {
      component.showContent = true;
    } else {
      component.showContent = false;
    }
  }

  handleSubstepChanges(): void {
    this.changeDetector.markForCheck();
    this.setProgressPercentage();
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getSubstepPosition(stepItem: ProgressStepItemComponent): number {
    // Takes text's line height into account
    const subStepOffsetPosition = 12;
    const subStepOffsetPositionLarge = 16;

    const progressSubstepItems = stepItem.progressSubsteps.toArray();

    // Current substep should not exceed number of substeps
    const substep = Math.min(
      stepItem.completedSubstep,
      progressSubstepItems.length,
    );

    const substepItem = progressSubstepItems[substep - 1];

    if (this.currentStep !== 0 && substepItem) {
      const substepOffset =
        window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10)
          ? subStepOffsetPosition
          : subStepOffsetPositionLarge;

      return (
        parseInt(substepItem.elementRef.nativeElement.offsetTop, 10) +
        substepOffset
      );
    }

    return 0;
  }

  /**
   * Recalculate progress bar only when the last step changes position
   */
  recalculateProgressBar(): void {
    if (!this.progressStepItem?.last?.elementRef?.nativeElement) {
      return;
    }

    const lastItemOffsetTop =
      this.progressStepItem.last.elementRef.nativeElement.offsetTop;

    if (this.previousOffsetTop !== lastItemOffsetTop) {
      setTimeout(() => {
        this.setProgressBarHeight();
        this.setProgressPercentage();
        this.changeDetector.markForCheck();
      });
      this.previousOffsetTop = lastItemOffsetTop;
    }
  }
}
